import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnInit
} from '@angular/core';
import { format, parseISO } from 'date-fns';
import { IrisButtonTypes, IrisButtonContainerMobileLayouts, IrisButtonContainerDesktopLayouts } from '@sondermind/iris/button';
import { IconColors, IconSizes } from '@sondermind/iris/icon';
import { BvErrorConfigFactory } from './bv-error-config.factory';
import {
  BvErrorData,
  BvErrorConfig,
  BvErrorTypes
} from './bv-error.types';

@Component({
  selector: 'client-bv-error',
  templateUrl: './bv-error.component.html',
  styleUrls: ['./bv-error.component.scss'],
  standalone: false
})
export class BvErrorComponent implements AfterViewInit, OnInit {
  @Input() data: BvErrorData | null = null;
  @Input() config: BvErrorConfig = {
    title: 'We found your info with a different insurance provider',
    subtitle: 'According to what we found, it looks like you\'re actually covered under a different insurance.',
    primaryButtonText: 'Continue with updated info',
    secondaryLinkText: 'continue without these updates',
    showMemberDetails: true
  };
  @Input() isMobile: boolean = false;

  @Output() primaryAction = new EventEmitter<void>();
  @Output() secondaryAction = new EventEmitter<void>();
  @Output() rejectSuggestedPayer = new EventEmitter<void>();
  @Output() tertiaryAction = new EventEmitter<void>(); // NEW

  @ViewChild('bvErrorTitle', { static: false }) bvErrorTitle?: ElementRef;

  IrisButtonTypes = IrisButtonTypes;
  IrisButtonContainerMobileLayouts = IrisButtonContainerMobileLayouts;
  IrisButtonContainerDesktopLayouts = IrisButtonContainerDesktopLayouts;
  IconColors = IconColors;
  IconSizes = IconSizes;
  BvErrorTypes = BvErrorTypes;

  private configProvided = false;

  ngOnInit(): void {
    // Auto-generate config based on error type if not provided
    if (this.data?.errorType && !this.configProvided) {
      this.config = BvErrorConfigFactory.createConfig(this.data.errorType, this.data);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.bvErrorTitle?.nativeElement?.scrollIntoView({ behavior: 'auto', block: 'center' });
      window.scrollBy(0, -150);
    }, 0);
  }

  get showMemberDetails(): boolean {
    const result = this.config.showMemberDetails &&
           this.data?.errorType === BvErrorTypes.SUGGESTED_PAYER &&
           !!this.data?.suggestedPayer;
    return result;
  }

  get showCoverageDetails(): boolean {
    return this.data?.errorType === BvErrorTypes.NO_COVERAGE;
  }

  get suggestedInsuranceName(): string {
    const name = this.data?.suggestedPayer?.name || '';
    return name;
  }

  formatSubtitle(): string {
    if (this.data?.coverageStartDate) {
      const startDate = parseISO(this.data.coverageStartDate);
      const formattedDate = format(startDate, 'MMM d, yyyy');
      return this.config.subtitle.replace('{coverageStartDate}', formattedDate);
    }

    if (this.data?.coverageEndDate) {
      const endDate = parseISO(this.data.coverageEndDate);
      const formattedDate = format(endDate, 'MMM d, yyyy');
      return this.config.subtitle.replace('{coverageEndDate}', formattedDate);
    }

    if (this.data?.suggestedPayer?.name) {
      return this.config.subtitle.replace('a different insurance', this.data.suggestedPayer.name);
    }

    return this.config.subtitle;
  }

  onPrimaryAction(): void {
    this.primaryAction.emit();
  }

  onSecondaryAction(): void {
    this.secondaryAction.emit();
  }

  onTertiaryAction(): void {
    this.tertiaryAction.emit();
  }

  onRejectSuggestedPayer(): void {
    this.rejectSuggestedPayer.emit();
  }
}
